import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { termsTitle, termsIntro, termsArticles } from '../content/termsContent';
import { useAuth } from '../context/AuthContext';
import { useMedicinePrices, toPriceGroups } from '../hooks/useMedicinePrices';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { Shield, Clock, X, MessageCircle, CheckCircle2, ShieldCheck, RefreshCcw, Send, ChevronRight, Gift } from 'lucide-react';
import { FaWeixin, FaEnvelope } from 'react-icons/fa';
import { SiLine } from 'react-icons/si';
import heroImage from '../assets/hero-image.jpg';
import dietImage from '../assets/service-diet.jpg';
import dietmanImage from '../assets/service-dietman.jpg';
import hairImage from '../assets/service-hair.jpg';
import agamanImage from '../assets/service-agaman.jpg';
import skincareImage from '../assets/service-skincare.jpg';
import wellnessImage from '../assets/service-wellness.jpg';
import edImage from '../assets/service-ed.jpg';
import step1Icon from '../assets/36.png';
import step2Icon from '../assets/38.png';
import step3Icon from '../assets/39.png';
import arrowIcon from '../assets/35.png';
import reasonDoctorCare from '../assets/lp-icons/01-doctor-care.webp';
import reasonOnlineBooking from '../assets/lp-icons/02-online-booking.webp';
import reasonClearPricing from '../assets/lp-icons/03-clear-pricing.webp';
import reasonPrivateDelivery from '../assets/lp-icons/04-private-delivery.webp';
import reasonApprovedMedicine from '../assets/lp-icons/05-approved-medicine.webp';
import reasonContinuousSupport from '../assets/lp-icons/06-continuous-support.webp';
import feeYenIcon from '../assets/lp-icons/fee-yen.svg';
import bookingCalendarIcon from '../assets/lp-icons/booking-calendar.svg';
import PaymentIcons from '../components/PaymentIcons';
import FullFaqModal from '../components/lp/FullFaqModal';
import Navbar from '../components/Navbar';
import LoginModal from '../components/LoginModal';
import EmailAuthModal from '../components/EmailAuthModal';


type CardItem = {
  title: string;
  body: string;
};

type ServiceItem = CardItem & {
  imageKey: 'diet' | 'dietman' | 'skincare' | 'hair' | 'agaman' | 'wellness' | 'ed';
  modal?: {
    description: string;
    meds: string[];
    priceGroups?: {
      name: string;
      variants: { dosage: string; single: string; sub: string | null }[];
    }[];
    features?: string[];
    recommendations?: string[];
  };
  detailLink?: string;
};

type TestimonialItem = {
  initials: string;
  name: string;
  age: string;
  rating: number;
  quote: string;
};

type StatItem = {
  label: string;
  value: string;
  unit: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

const serviceImages = {
  diet: dietImage,
  dietman: dietmanImage,
  skincare: skincareImage,
  hair: hairImage,
  agaman: agamanImage,
  wellness: wellnessImage,
  ed: edImage,
};

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'ja';

  // ログイン後のリダイレクト先（?redirectTo= / state.from / sessionStorage / デフォルト /mypage）
  // /mypage/profile への自動リダイレクトは避ける（プロフィール未入力ガードによる意図しない遷移を防止）
  const _initRedirect = (() => {
    const params = new URLSearchParams(location.search);
    // 通常の ?redirectTo= パラメータ
    let redirectTo: string | null = params.get('redirectTo');
    // LIFF経由の場合、パラメータは liff.state に格納される
    // 例: ?liff.state=%3FredirectTo%3D%252Freservation
    if (!redirectTo) {
      const liffState = params.get('liff.state');
      if (liffState) {
        const stateParams = new URLSearchParams(
          liffState.startsWith('?') ? liffState.slice(1) : liffState
        );
        redirectTo = stateParams.get('redirectTo');
      }
    }
    const raw: string = redirectTo || (location.state as any)?.from?.pathname || '/mypage';
    return raw === '/mypage/profile' ? '/mypage' : raw;
  })();
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(_initRedirect);

  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);
  // 料金表ポップアップの価格を DB(API)優先に。detailLink(/menu/xxx)から service_type を導出して取得し、
  // DB にデータが無ければ locale の priceGroups にフォールバックする（表示が壊れない）。
  const _rawModalServiceType = selectedService?.detailLink
    ? selectedService.detailLink.replace(/^\/menu\//, '').replace(/-/g, '_')
    : '';
  // detailLink→DB分類のズレを吸収。例: /menu/medical-skincare → 'medical_skincare' だが DB分類は 'skincare'。
  const SERVICE_TYPE_ALIAS: Record<string, string> = { medical_skincare: 'skincare' };
  const modalServiceType = SERVICE_TYPE_ALIAS[_rawModalServiceType] || _rawModalServiceType;
  const { medicines: modalMedicines } = useMedicinePrices(modalServiceType, currentLang);
  const modalPriceGroups = toPriceGroups(modalMedicines, currentLang);
  const [infoModal, setInfoModal] = useState<'operator' | 'privacy' | 'legal' | 'terms' | 'contact' | null>(null);
  const [showFullPrivacy, setShowFullPrivacy] = useState(false);

  // Contact Form State
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [openPriceIndex, setOpenPriceIndex] = useState<number | null>(0);
  const [openPriceVariant, setOpenPriceVariant] = useState<string | null>('0-0');
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [showWechatModal, setShowWechatModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showEmailAuthModal, setShowEmailAuthModal] = useState(false);
  const [emailAuthDefaultTab, setEmailAuthDefaultTab] = useState<'login' | 'register'>('login');
  const [showInviteModal, setShowInviteModal] = useState(false);

  // /login パスで直接アクセスされた場合 OR 401 強制ログアウト後のフラグを検知してログインモーダルを表示
  // sessionStorage フラグを使うことでリフレッシュ時に再表示されるバグを防ぐ
  useEffect(() => {
    const expired = sessionStorage.getItem('auth_expired');
    if (expired) {
      sessionStorage.removeItem('auth_expired');
      const storedRedirect = sessionStorage.getItem('auth_expired_redirect') || '/mypage';
      sessionStorage.removeItem('auth_expired_redirect');
      const sanitized = storedRedirect === '/mypage/profile' ? '/mypage' : storedRedirect;
      setRedirectAfterLogin(sanitized);
      toast.info(t('auth.session_expired'));
      setShowLoginModal(true);
    } else if (location.pathname === '/login') {
      setShowLoginModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?ref= / ?lc= パラメータを検知して紹介モーダルを表示
  useEffect(() => {
    const ref = searchParams.get('ref');
    const lc = searchParams.get('lc');
    if (ref) {
      sessionStorage.setItem('pending_ref_token', ref);
    }
    if (lc) {
      document.cookie = `ref_lc=${encodeURIComponent(lc.trim().toLowerCase())}; path=/; max-age=3600; SameSite=Lax`;
    }
    if (ref || lc) {
      setShowInviteModal(true);
    }
  }, [searchParams]);

  // ?coupon= パラメータを検知してクーポンを処理
  // - ログイン済み: 直接バインド API を呼ぶ
  // - 未ログイン : sessionStorage に保存してログインモーダルを表示
  useEffect(() => {
    const couponCode = searchParams.get('coupon');
    if (!couponCode) return;
    const upper = couponCode.toUpperCase();
    if (isAuthenticated) {
      // 既ログイン → 即バインド
      api.post('/user/coupons/bind', { code: upper })
        .then(() => toast.success(t('auth.coupon_bound', { code: upper })))
        .catch(() => { /* バインド済み / 期限切れは無視 */ });
    } else {
      sessionStorage.setItem('pending_coupon_code', upper);
      toast.info(t('auth.coupon_pending', { code: upper }));
      setShowLoginModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ログイン後に pending_coupon_code があれば自動バインドする共通ヘルパー
  const bindPendingCoupon = async () => {
    const code = sessionStorage.getItem('pending_coupon_code');
    if (!code) return;
    sessionStorage.removeItem('pending_coupon_code');
    try {
      await api.post('/user/coupons/bind', { code });
      toast.success(t('auth.coupon_bound', { code }));
    } catch {
      // 既バインド or 期限切れは静かに無視
    }
  };

  const [contactStep, setContactStep] = useState<'guide' | 'form'>('guide');
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.matchMedia('(min-width: 1024px)').matches);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // ---------- WeChat MP OAuth LOGIC ----------
  /**
   * 微信内置浏览器判定
   * WeChat App 内の WebView は UA に "MicroMessenger" を含む
   */
  const isInWechat = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    return /MicroMessenger/i.test(navigator.userAgent);
  };

   /**
    * zh モードの「立即咨询」ハンドラ:
    *   - 微信内置ブラウザ or 手机浏览器 -> 公众号 OAuth 授权（尝试唤起微信）
    *   - PC 浏览器 -> 客服/公众号 QR 弹窗
    */
  const handleWechatConsult = () => {
    if (isInWechat()) {
      // 在微信内置浏览器内，直接走公众号快捷登录
      window.location.href = '/api/wechat-mp/login';
    } else {
      // 在外部浏览器（PC或手机），弹出引导弹窗
      setShowWechatModal(true);
    }
  };

  const handleWechatPCLogin = () => {
    window.location.href = '/api/wechat/login';
  };

  // 招待リンクからのWeChat ログイン（ref_token を state に埋め込む）
  const handleWechatInviteLogin = () => {
    const activeRef = sessionStorage.getItem('pending_ref_token');
    const refParam = activeRef ? `?ref=${encodeURIComponent(activeRef)}` : '';
    window.location.href = isInWechat()
      ? `/api/wechat-mp/login${refParam}`
      : `/api/wechat/login${refParam}`;
  };
  // -------------------------------------------

  // ---------- LINE LIFF LOGIN LOGIC ----------
  useEffect(() => {
    const initLiff = async () => {
      const liffId = process.env.REACT_APP_LINE_LIFF_ID;
      if (!liffId) return;
      try {
        const liff = (await import('@line/liff')).default;
        await liff.init({ liffId });
        // LIFF内ブラウザで未ログインの場合、自動的にLINEログインを促す
        if (liff.isInClient() && !liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href });
          return;
        }
        if (liff.isLoggedIn()) {
          const idToken = liff.getIDToken();
          // LIFF経由かつすでにバックエンドJWTを持っている場合 → 直接対象ページへ遷移
          if (liff.isInClient() && localStorage.getItem('token')) {
            navigate(redirectAfterLogin, { replace: true });
            return;
          }
          // もしバックエンドのJWTがなくて、LINEのidTokenがあるなら自動ログイン試行
          if (idToken && !localStorage.getItem('token')) {
            try {
              const pendingRef = sessionStorage.getItem('pending_ref_token');
              const res = await api.post('/auth/line', { id_token: idToken, ref_token: pendingRef || undefined });
              login(res.data.access_token, res.data.role, 'line');
              if (pendingRef) sessionStorage.removeItem('pending_ref_token');
              await bindPendingCoupon();
              if (res.data.is_new_user) {
                setTimeout(() => {
                  navigate('/welcome', { replace: true });
                }, 500);
              } else {
                toast.success(t('auth.toast_login_success') || 'Login successful');
                setTimeout(() => {
                  navigate(redirectAfterLogin, { replace: true });
                }, 1500);
              }
            } catch (err: any) {
              console.error('Auto LINE login failed', err);
              // 401 (Expired or Invalid) の場合は、LIFF側も一度ログアウトしてクリーンにする
              if (err.response?.status === 401) {
                liff.logout();
              }
            }
          }
        }
      } catch (error) {
        console.error('LIFF initialization failed', error);
      }
    };
    initLiff();
  }, []);

  const handleLineLogin = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const liffId = process.env.REACT_APP_LINE_LIFF_ID;
    
    if (!liffId) {
      toast.error('LINE LIFF ID is not configured');
      return;
    }
    
    try {
      const liff = (await import('@line/liff')).default;
      try {
        await liff.init({ liffId });
      } catch (e) {
        // ignore already initialized error
      }
      
      if (!liff.isLoggedIn()) {
        liff.login();
        return;
      }
      const idToken = liff.getIDToken();
      if (!idToken) throw new Error("No ID Token");
      
      try {
        const pendingRef = sessionStorage.getItem('pending_ref_token');
        const res = await api.post('/auth/line', { id_token: idToken, ref_token: pendingRef || undefined });
        login(res.data.access_token, res.data.role, 'line');
        if (pendingRef) sessionStorage.removeItem('pending_ref_token');
        await bindPendingCoupon();
        if (res.data.is_new_user) {
          setTimeout(() => {
            navigate('/welcome', { replace: true });
          }, 500);
        } else {
          toast.success(t('auth.toast_login_success') || 'Login successful');
          setTimeout(() => {
            navigate(redirectAfterLogin, { replace: true });
          }, 1500);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          // Token expired or mismatched
          liff.logout();
          toast.error(t('auth.session_expired') || 'Session expired, please login again');
        } else {
          throw err;
        }
      }
    } catch (err: any) {
      console.error('LINE Login Error:', err);
      toast.error('LINE Login failed');
    }
  };
  // -------------------------------------------

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName || !contactEmail || !contactMessage) {
      toast.error(t('contact.error_required'));
      return;
    }
    if (!isValidEmail(contactEmail)) {
      toast.error(t('contact.error_invalid_email'));
      return;
    }
    if (contactMessage.trim().length < 10) {
      toast.error(t('contact.error_short_message'));
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/contact', { name: contactName, email: contactEmail, message: contactMessage });
      toast.success(t('contact.success'));
      setContactName('');
      setContactEmail('');
      setContactMessage('');
      setInfoModal(null);
    } catch {
      toast.error(t('contact.error_generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTaxIncludedPrice = (priceStr: string) => {
    const num = parseInt(priceStr.replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    const taxNum = Math.floor(num * 1.1);
    const formatted = taxNum.toLocaleString();
    return currentLang === 'ja' ? `${formatted}円` : `${formatted} 日元`;
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedService) {
      document.body.style.overflow = 'hidden';
      setOpenPriceIndex(0); // Reset accordion state when opening a new service
      setOpenPriceVariant('0-0');
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedService]);

  const reasons = t('lp.reasons.cards', { returnObjects: true }) as CardItem[];
  const serviceCards = (t('lp.services.cards', { returnObjects: true }) as ServiceItem[]).filter(
    (card) => card.imageKey !== 'agaman' && card.imageKey !== 'hair'
  );
  const testimonials = useMemo(() => {
    const base = t('lp.testimonials.cards', { returnObjects: true }) as TestimonialItem[];
    // Use a pseudo-random shuffle that stays stable for the component's lifecycle
    return [...base].sort(() => 0.5 - Math.random());
  }, [t]);
  const faqItems = t('lp.faq.items', { returnObjects: true }) as FaqItem[];
  const trustItems = t('lp.hero.trustItems', { returnObjects: true }) as string[];

  const changeLang = (lang: 'ja' | 'zh') => {
    i18n.changeLanguage(lang);
    window.localStorage.setItem('i18nextLng', lang);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>{currentLang === 'ja' ? 'オンライン診療予約 | Melodis Clinic' : '线上问诊预约 | Melodis Clinic'}</title>
        <meta name="description" content={currentLang === 'ja' ? 'Melodis Clinicは、スマホで完結するオンライン診療サービスです。メディカルダイエット、ED・男性アンチエイジングなどの診察からお薬の処方・配送まで、最短当日・誰にも知られずスムーズに受診できます。' : 'Melodis Clinic提供全流程手机端线上问诊服务。涵盖医疗减重、护肤、ED・男性抗衰老等。线上开方，最快次日发货，隐私包装，让就医更轻松、便捷。'} />
        <meta name="keywords" content={currentLang === 'ja' ? 'オンライン診療, メディカルダイエット, マンジャロ, リベルサス, ED・男性アンチエイジング, ピル, Melodis Clinic' : '线上问诊, 医疗减重, 替尔泊肽注射液 (Mounjaro), 口服司美格鲁肽 (Rybelsus), 护肤, ED・男性抗衰老, 避孕药, Melodis Clinic'} />
        
        {/* Open Graph Tags for Social Media & Messengers */}
        <meta property="og:title" content={currentLang === 'ja' ? 'Melodis Clinic | オンライン診療' : 'Melodis Clinic | 线上问诊'} />
        <meta property="og:description" content={currentLang === 'ja' ? 'スマホひとつで、いつでもどこからでも診療予約。最短翌日にお薬を発送します。' : '手机一键预约线上问诊。医生在线开具处方，最快次日发货。'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://melodis-clinic.com/lp" />
        <meta property="og:image" content="https://melodis-clinic.com/images/Melodislogo.png" />
        <meta property="og:site_name" content="Melodis Clinic" />
        
        {/* JSON-LD Schema suitable for AI Assistants & SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalClinic",
            "name": "Melodis Clinic",
            "description": currentLang === 'ja' ? '全オンライン完結型のクリニック。メディカルダイエット、美容内服、ED・男性アンチエイジングなどを提供します。' : '全流程线上问诊诊所，提供医疗减重、护肤内服、ED・男性抗衰老等服务。',
            "url": "https://melodis-clinic.com",
            "logo": "https://melodis-clinic.com/images/Melodislogo.png",
            "image": "https://melodis-clinic.com/images/Melodislogo.png",
            "telephone": "+81-3-6908-6228",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "3-23-3 Higashi-Ikebukuro, 804",
              "addressLocality": "Toshima-ku",
              "addressRegion": "Tokyo",
              "postalCode": "170-0013",
              "addressCountry": "JP"
            },
            "medicalSpecialty": [
              "Diet",
              "Dermatology",
              "Urology"
            ],
            "availableService": [
              {
                "@type": "MedicalTherapy",
                "name": currentLang === 'ja' ? "メディカルダイエット・男性/女性" : "医疗减重・男性/女性"
              },
              {
                "@type": "MedicalTherapy",
                "name": currentLang === 'ja' ? "美容内服・スキンケア" : "医疗护肤内服"
              },
              {
                "@type": "MedicalTherapy",
                "name": currentLang === 'ja' ? "ED・男性アンチエイジング" : "ED・男性抗衰老"
              }
            ]
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Melodis Clinic Online Consultation",
            "audience": {
              "@type": "Patient",
              "healthCondition": {
                "@type": "MedicalCondition",
                "name": "Obesity, Skin problems, Erectile Dysfunction"
              }
            },
            "about": [
              {
                "@type": "Drug",
                "name": "替尔泊肽注射液 (Mounjaro)"
              },
              {
                "@type": "Drug",
                "name": "口服司美格鲁肽 (Rybelsus)"
              }
            ]
          })}
        </script>
      </Helmet>
      <Navbar
        ipAllowed={true}
        simple={true}
        onLoginClick={() => currentLang === 'zh' ? handleWechatConsult() : setShowLoginModal(true)}
        onContactClick={() => {
          setContactStep('guide');
          if (currentLang === 'zh') {
            setInfoModal('contact');
          } else {
            const contactSection = document.getElementById('contact');
            if (contactSection) {
              contactSection.scrollIntoView({ behavior: 'smooth' });
            } else {
              setInfoModal('contact');
            }
          }
        }}
      />

      <main>
        <section className="relative overflow-hidden bg-gradient-hero">
          <div className="container relative grid gap-8 lg:gap-12 pt-20 pb-12 lg:pt-24 lg:pb-16 xl:pt-32 xl:pb-24 lg:grid-cols-[1fr,0.92fr] lg:items-stretch">
            <div className="animate-fade-up flex flex-col justify-center min-w-0">
              <div className="flex flex-wrap gap-3">
                {/* Badge 1: Shipping */}
                <div className="relative inline-flex items-center overflow-hidden rounded-full border border-coral/15 bg-white/80 px-4 py-2 text-sm font-semibold text-primary shadow-soft">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_40%,rgba(255,255,255,0.6)_50%,transparent_60%)] bg-[length:200%_100%] -translate-x-full animate-shine" />
                  </div>
                  {t('lp.hero.badge')}
                </div>

                {/* Badge 2: Free Consultation */}
                <div className="relative inline-flex items-center overflow-hidden rounded-full bg-coral px-4 py-2 text-sm font-bold text-white shadow-soft">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_40%,rgba(255,255,255,0.3)_50%,transparent_60%)] bg-[length:200%_100%] -translate-x-full animate-shine" />
                  </div>
                  {t('lp.hero.badge_free')}
                </div>
              </div>

              <h1 className="mt-6 lg:mt-8 text-[2.25rem] sm:text-5xl font-bold leading-[1.15] md:leading-[1.2] tracking-tight text-navy lg:text-6xl xl:text-7xl break-keep">
                <span className="block">{t('lp.hero.title.line1')}</span>
                <span className="block text-coral">{t('lp.hero.title.line2')}</span>
                <span className="block">{t('lp.hero.title.line3')}</span>
              </h1>
              <p className="mt-4 text-base font-semibold tracking-[0.15em] text-coral/80 uppercase">
                Melodis Clinic
              </p>

              <p className="mt-5 lg:mt-8 max-w-2xl text-lg leading-9 text-muted-foreground">
                {t('lp.hero.description')}
              </p>

              <div className="mt-5 lg:mt-8 flex flex-col gap-4 sm:flex-row">
                {isAuthenticated ? (
                  <button
                    onClick={() => navigate('/reserve')}
                    className="inline-flex items-center justify-center rounded-[1.4rem] bg-gradient-primary px-8 py-4 text-lg font-semibold text-white shadow-card transition-transform hover:-translate-y-0.5"
                  >
                    {t('navbar.reserve') || (currentLang === 'ja' ? '診療予約' : '诊疗预约')}
                  </button>
                ) : currentLang === 'zh' ? (
                  <button
                    type="button"
                    onClick={() => handleWechatConsult()}
                    className="inline-flex items-center justify-center rounded-[1.4rem] bg-gradient-primary px-8 py-4 text-lg font-semibold text-white shadow-card transition-transform hover:-translate-y-0.5"
                  >
                    {t('lp.hero.primary')}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="inline-flex items-center justify-center rounded-[1.4rem] bg-gradient-primary px-8 py-4 text-lg font-semibold text-white shadow-card transition-transform hover:-translate-y-0.5"
                  >
                    {t('lp.hero.primary')}
                  </button>
                )}
                <a
                  href="#services"
                  className="inline-flex items-center justify-center rounded-[1.4rem] border border-border bg-white px-8 py-4 text-lg font-semibold text-navy shadow-soft transition hover:bg-secondary"
                >
                  {t('lp.hero.secondary')}
                </a>
              </div>

              <div className="mt-5 lg:mt-10 flex flex-col gap-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:gap-8">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-accent" />
                  <span>{trustItems[0]}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" />
                  <span>{trustItems[1]}</span>
                </div>
              </div>
            </div>

            <div className="animate-fade-up stagger-2 flex items-center">
              <div className="relative mx-auto max-w-[40rem] w-full">
                <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(180deg,#f58d7f_0%,#f4a191_42%,#b8c89e_100%)] p-0 shadow-elevated aspect-[4/3]">
                  <img
                    src={heroImage}
                    alt={t('lp.hero.imageAlt')}
                    className="h-full w-full object-cover"
                    loading="eager"
                  />
                </div>

                <div className="absolute left-0 sm:-left-6 -bottom-10 sm:-bottom-4 rounded-full bg-white px-5 py-3 shadow-elevated animate-float flex items-center gap-3 border border-coral/10">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-coral/10 text-coral">
                    <img src={feeYenIcon} alt="" className="h-5 w-5" />
                  </div>
                  <div className="flex items-baseline gap-2 whitespace-nowrap px-1">
                    <span className="text-2xl font-black text-coral leading-none">{t('lp.hero.floatingLabel')}</span>
                    <span className="text-2xl font-black text-coral leading-none">{t('lp.hero.floatingValue')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="mx-auto max-w-3xl text-center mb-16">
              <div className="inline-flex rounded-full bg-coral/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-coral mb-6">
                {t('lp.services.eyebrow')}
              </div>
              <h2 className="text-[1.75rem] sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
                {currentLang === 'ja' ? (
                  <>
                    あなたの悩みに合わせた<br />
                    <span className="text-coral">専門メニュー</span>
                  </>
                ) : (
                  <>
                    根据您的烦恼定制<br />
                    <span className="text-coral">专业诊疗菜单</span>
                  </>
                )}
              </h2>
              <p className="mt-4 text-base leading-7 text-muted-foreground">{t('lp.services.description')}</p>
            </div>
            <div className="mt-8 sm:mt-12 grid gap-4 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 max-w-[1400px] mx-auto">
              {serviceCards.map((card, index) => (
                <article 
                  key={card.title} 
                  id={card.imageKey}
                  className={`group flex flex-row sm:flex-col overflow-hidden rounded-[1.25rem] sm:rounded-[1.75rem] border border-border bg-white shadow-card animate-fade-up stagger-${(index % 5) + 1} cursor-pointer hover:border-coral-light transition-all duration-300 hover:scale-[1.02] sm:hover:scale-[1.03] hover:shadow-elevated h-full text-left scroll-mt-24`}
                  onClick={() => {
                    setSelectedService(card);
                  }}
                >
                  <div className="w-2/5 sm:w-full aspect-[4/5] sm:aspect-[4/3] overflow-hidden bg-slate-50 flex-shrink-0">
                    <img 
                      src={serviceImages[card.imageKey]} 
                      alt={card.title} 
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" 
                      style={{ 
                        imageRendering: 'crisp-edges',
                        WebkitImageRendering: '-webkit-optimize-contrast'
                      } as any}
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col flex-grow p-4 sm:p-6 justify-center">
                    <h3 className="text-lg sm:text-xl font-semibold text-navy leading-tight">{card.title}</h3>
                    <div className="mt-2 sm:mt-3 flex-grow sm:min-h-[5rem] lg:min-h-[7rem]">
                      <p className="text-xs sm:text-sm leading-6 sm:leading-7 text-muted-foreground line-clamp-3 sm:line-clamp-none">{card.body}</p>
                    </div>
                    <p className="mt-2 sm:mt-4 text-xs sm:text-sm font-semibold text-primary group-hover:underline">
                      {currentLang === 'ja' ? '詳細を見る' : '查看详情'} &rarr;
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="reasons" className="bg-[#fff9f8] py-20 md:py-28">
          <div className="container mx-auto px-6">
            <div className="mx-auto max-w-3xl text-center mb-16">
              <div className="inline-flex rounded-full bg-coral/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-coral mb-6">
                {t('lp.reasons.eyebrow')}
              </div>
              <h2 className="text-[1.75rem] sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">{t('lp.reasons.title')}</h2>
              <p className="mt-6 text-base sm:text-lg text-slate-500 font-medium">{t('lp.reasons.subtitle')}</p>
            </div>
            
            <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {reasons.map((item, index) => {
                const images = [
                  reasonDoctorCare,
                  reasonOnlineBooking,
                  reasonClearPricing,
                  reasonPrivateDelivery,
                  reasonApprovedMedicine,
                  reasonContinuousSupport,
                ];
                return (
                   <article
                    key={item.title}
                    className="rounded-[2rem] bg-white p-6 sm:p-10 shadow-soft border border-white transition-all duration-300 hover:shadow-elevated hover:-translate-y-1 flex flex-col items-center sm:items-start text-center sm:text-left"
                  >
                    <div className="mb-6 w-full">
                      <img
                        src={images[index % images.length]}
                        alt=""
                        className="w-full h-auto object-contain"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy mb-4 tracking-tight">{item.title}</h3>
                    <p className="text-xs sm:text-sm leading-6 sm:leading-7 text-slate-500 font-medium">{item.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="flow" className="bg-[#fffdfd] py-20 md:py-28 overflow-hidden">
          <div className="container px-6">
            <div className="mx-auto max-w-3xl text-center mb-16 md:mb-24">
              <div className="inline-flex rounded-full bg-coral/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-coral mb-6">
                {t('lp.flow.eyebrow')}
              </div>
              <h2 className="text-[1.75rem] sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">{t('lp.flow.title')}</h2>
            </div>
            
            <div className="relative mx-auto max-w-5xl">
              {/* Flex Container for Steps + Arrows */}
              <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-12 md:gap-4 relative w-full pt-8">
                {[
                  { icon: step1Icon, step: '01' },
                  { icon: step2Icon, step: '02' },
                  { icon: step3Icon, step: '03' }
                ].map((meta, idx) => {
                  const items = t('lp.flow.steps', { returnObjects: true }) as Array<{ title: string; body: string }>;
                  const item = items[idx];
                  return (
                    <React.Fragment key={meta.step}>
                      {/* Step Card */}
                      <div className="group relative flex flex-col items-center w-full md:w-1/3">
                        <div className="relative mb-10 h-44 w-44 md:h-52 md:w-52 flex items-center justify-center rounded-[3rem] bg-white border border-coral/10 shadow-soft transition-all duration-500 group-hover:scale-105 group-hover:shadow-elevated group-hover:border-coral/20">
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-primary text-white text-[10px] sm:text-xs font-black tracking-widest shadow-card flex items-center justify-center whitespace-nowrap">
                            STEP {meta.step}
                          </div>
                          <img 
                            src={meta.icon} 
                            alt="" 
                            className="h-32 w-32 md:h-40 md:w-40 object-contain drop-shadow-sm select-none" 
                            draggable={false}
                          />
                        </div>
                        <div className="text-center px-4 w-full">
                          <h3 className="text-xl md:text-2xl font-bold text-navy mb-4 tracking-tight">{item.title}</h3>
                          <p className="text-sm leading-7 text-slate-500 font-medium whitespace-pre-line">{item.body}</p>
                        </div>
                        
                        {/* Mobile Arrow */}
                        {idx < 2 && (
                          <div className="mt-12 md:hidden">
                            <img src={arrowIcon} alt="" className="h-6 w-auto opacity-20 rotate-90" />
                          </div>
                        )}
                      </div>

                      {/* Desktop Arrow */}
                      {idx < 2 && (
                        <div className="hidden md:flex flex-shrink-0 h-44 md:h-52 w-12 justify-center items-center pointer-events-none">
                          <img src={arrowIcon} alt="" className="h-4 w-auto opacity-30 select-none" />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Payment Methods Integration */}
            <div className="mt-20 md:mt-32 pt-16 border-t border-coral/5 animate-fade-up stagger-5">
              <PaymentIcons />
            </div>
          </div>
        </section>

        {/* Testimonials section hidden until real user feedback is available */}
        {/* <section className="bg-[#fffdfd] py-16 md:py-28">
           ... (removed content) ...
        </section> */}

        <section id="faq" className="bg-white py-20 md:py-32">
          <div className="container px-6">
            <div className="mx-auto max-w-3xl text-center mb-16">
              <div className="inline-flex rounded-full bg-coral/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-coral mb-6">
                {t('lp.faq.eyebrow')}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-navy tracking-tight">{t('lp.faq.title')}</h2>
            </div>
            
            <div className="mx-auto max-w-4xl space-y-4">
              {faqItems.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`group rounded-[2rem] border transition-all duration-300 ${
                    openFaqIndex === idx ? 'border-coral/20 bg-coral/5 shadow-soft' : 'border-border bg-white hover:border-coral/10 shadow-sm'
                  }`}
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                    className="flex w-full items-center justify-between px-5 sm:px-8 py-7 text-left"
                  >
                    <span className="text-lg font-bold text-navy pr-8 leading-tight">
                      {item.question}
                    </span>
                    <div className={`flex-shrink-0 transition-transform duration-300 ${openFaqIndex === idx ? 'rotate-180' : ''}`}>
                      <svg className={`h-6 w-6 ${openFaqIndex === idx ? 'text-coral' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openFaqIndex === idx ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-8 pb-8 text-base leading-8 text-navy/70 font-medium">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center flex flex-col items-center gap-6">
              <button
                onClick={() => setIsFaqModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-8 py-4 text-lg font-bold text-white shadow-soft transition-transform hover:-translate-y-1"
              >
                {t('lp.faq.viewAll', 'すべての質問を見る')}
              </button>
              <p className="text-sm font-medium text-navy/50">
                {t('lp.faq.moreInLine')}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-gradient-primary py-24 md:py-32">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-8">
              {t('lp.cta.title')}
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-white/90 leading-relaxed mb-12 whitespace-pre-line">
              {t('lp.cta.description')}
            </p>
            <div className="flex justify-center">
              {currentLang === 'zh' ? (
                <button
                  type="button"
                  onClick={() => handleWechatConsult()}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-white px-10 py-5 text-xl font-bold text-coral shadow-elevated transition-transform hover:-translate-y-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={24} height={24}><path d="M8.5 10a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12 10a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" /><path fillRule="evenodd" d="M2.25 4.5A2.25 2.25 0 0 0 0 6.75v6a2.25 2.25 0 0 0 2.25 2.25h.75v2.25a.75.75 0 0 0 1.28.53l2.47-2.47h7.5A2.25 2.25 0 0 0 16.5 13.5v-.75h.5a.75.75 0 0 0 0-1.5h-.5V6.75A2.25 2.25 0 0 0 14.25 4.5H2.25ZM1.5 6.75A.75.75 0 0 1 2.25 6h12a.75.75 0 0 1 .75.75v6a.75.75 0 0 1-.75.75H5.25a.75.75 0 0 1-.53.22L3 15.44V14.25h-.75a.75.75 0 0 1-.75-.75v-6Z" clipRule="evenodd" /></svg>
                  {t('lp.cta.primary')}
                </button>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-white px-10 py-5 text-xl font-bold text-coral shadow-elevated transition-transform hover:-translate-y-1"
                >
                  <SiLine size={24} />
                  {t('lp.cta.primary')}
                </button>
              )}
            </div>
          </div>
        </section>
        <FullFaqModal 
          isOpen={isFaqModalOpen} 
          onClose={() => setIsFaqModalOpen(false)} 
        />
      </main>

      <footer className="border-t border-border bg-[#fff9f8]">
        <div className="container py-14">
          <div className="grid gap-10 md:grid-cols-[1.2fr,1fr,1fr,1fr]">
            <div>
              <img src="/images/Melodislogo.png" alt="Melodis Clinic" className="h-12 w-auto" />
              <p className="mt-5 max-w-sm text-sm leading-7 text-slate-500">{t('lp.footer.description')}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-coral">Services</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-500">
                {serviceCards.map((service, idx) => (
                  <li key={idx}>
                    <button 
                      onClick={() => setSelectedService(service)}
                      className="hover:text-coral transition-colors text-left"
                    >
                      {service.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-coral">Support</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-500">
                <li><a href="#flow" className="hover:text-coral transition-colors">{t('lp.footer.support.0')}</a></li>
                <li><a href="#faq" className="hover:text-coral transition-colors">{t('lp.footer.support.1')}</a></li>
                <li className="hover:text-coral transition-colors">
                  {localStorage.getItem('token') ? (
                    <a href="/mypage" className="hover:text-coral transition-colors text-left block">
                      {t('navbar.mypage')}
                    </a>
                  ) : currentLang === 'zh' ? (
                    <button onClick={() => handleWechatConsult()} className="hover:text-coral transition-colors text-left">{t('lp.footer.support.2')}</button>
                  ) : (
                    <button onClick={() => setShowLoginModal(true)} className="hover:text-coral transition-colors text-left">{t('lp.footer.support.2')}</button>
                  )}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-coral">Company</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-500">
                <li>
                  <button onClick={() => setInfoModal('operator')} className="hover:text-coral transition-colors text-left uppercase">
                    {t('lp.footer.company.0')}
                  </button>
                </li>
                <li>
                  <button onClick={() => setInfoModal('privacy')} className="hover:text-coral transition-colors text-left uppercase">
                    {t('lp.footer.company.1')}
                  </button>
                </li>
                <li>
                  <button onClick={() => setInfoModal('legal')} className="hover:text-coral transition-colors text-left uppercase">
                    {t('lp.footer.company.2')}
                  </button>
                </li>
                <li>
                  <button onClick={() => setInfoModal('terms')} className="hover:text-coral transition-colors text-left uppercase">
                    {termsTitle[currentLang]}
                  </button>
                </li>
                <li>
                  <button onClick={() => {
                    setContactStep('guide');
                    setInfoModal('contact');
                  }} className="hover:text-coral transition-colors text-left uppercase">
                    {t('lp.footer.company.3')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-14 border-t border-slate-200 pt-10 text-center">
            <div className="flex flex-col items-center gap-4 text-xs text-slate-400 font-medium">
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-1 rounded-full border border-slate-200 px-5 py-2 text-xs font-semibold text-slate-400 hover:border-coral/40 hover:text-coral transition-all"
                aria-label="Scroll to top"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l5 5a1 1 0 11-1.414 1.414L11 6.414V17a1 1 0 11-2 0V6.414L5.707 9.707a1 1 0 11-1.414-1.414l5-5A1 1 0 0110 3z" clipRule="evenodd"/></svg>
                TOP
              </button>
              <p className="whitespace-pre-wrap sm:whitespace-normal">{t('lp.footer.supervision')}</p>
              <p className="whitespace-pre-wrap sm:whitespace-normal px-4">{t('lp.footer.note')}</p>
              <p className="mt-2">{t('lp.footer.copyright')}</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Button — WeChat/LINE for guests, Direct Reserve for logged-in users */}
      {isAuthenticated ? (
        <button
          type="button"
          onClick={() => navigate('/reserve')}
          className="fixed bottom-6 right-6 flex items-center justify-between pl-4 pr-6 h-16 bg-white border-2 border-coral rounded-full text-coral shadow-elevated hover:scale-105 active:scale-95 transition-all z-40 gap-4 group"
          aria-label={t('lp.cta.floating_reserve')}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-coral/10 group-hover:bg-coral/20 transition-colors">
            <img src={bookingCalendarIcon} alt="" className="w-[22px] h-[22px] group-hover:rotate-12 transition-transform" />
          </div>
          <span className="font-bold text-[15px] tracking-tight whitespace-nowrap">
            {t('lp.cta.floating_reserve')}
          </span>
          <ChevronRight size={18} className="ml-2 opacity-50 group-hover:translate-x-1 transition-transform" />
        </button>
      ) : currentLang === 'zh' ? (
        <button
          type="button"
          onClick={() => handleWechatConsult()}
          className="fixed bottom-6 right-6 flex items-center justify-between pl-2 pr-6 h-16 bg-[#07c160] rounded-full text-white shadow-elevated hover:scale-105 active:scale-95 transition-all z-40 gap-4 group"
          aria-label={t('lp.header.wechat')}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 ml-1">
             <FaWeixin size={28} />
          </div>
          <span className="font-bold text-[15px] tracking-tight whitespace-nowrap">
            {t('lp.cta_floating')}
          </span>
          <ChevronRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setShowLoginModal(true)}
          className="fixed bottom-6 right-6 flex items-center justify-between pl-2 pr-6 h-16 bg-[#06c755] rounded-full text-white shadow-elevated hover:scale-105 active:scale-95 transition-all z-40 gap-4 group"
          aria-label={t('lp.header.line')}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 ml-1">
             <SiLine size={24} />
          </div>
          <span className="font-bold text-[15px] tracking-tight whitespace-nowrap">
            {t('lp.cta_floating')}
          </span>
          <ChevronRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLineLogin={handleLineLogin}
        onEmailLogin={() => { setShowLoginModal(false); setShowEmailAuthModal(true); }}
      />
      <EmailAuthModal
        isOpen={showEmailAuthModal}
        onClose={() => { setShowEmailAuthModal(false); setEmailAuthDefaultTab('login'); }}
        redirectTo={redirectAfterLogin}
        defaultTab={emailAuthDefaultTab}
      />

      {/* WeChat Contact Modal */}
      {showWechatModal && (
        <div className="fixed inset-0 z-[1000] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={() => setShowWechatModal(false)} />
          <div className="relative z-50 w-full max-w-sm overflow-hidden rounded-3xl bg-coral shadow-elevated animate-in zoom-in-95 duration-200">
            <div className="px-8 pt-8 pb-4 flex justify-between items-center bg-coral text-white border-b border-coral rounded-t-3xl">
              <h3 className="text-2xl font-bold">{t('modal.contact.wechat_title')}</h3>
              <button onClick={() => setShowWechatModal(false)} className="rounded-full p-2 bg-white/20 text-white hover:bg-white/30 transition-all">
                <X size={20} />
              </button>
            </div>
            <div className="px-8 py-8 flex flex-col items-center gap-6 text-center bg-white rounded-b-3xl">
              <div className="w-48 h-48 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 text-sm overflow-hidden">
                {/* WeChat QR code placeholder – replace src with actual QR image */}
                <img
                  src="/images/wechat_mp_qr.jpg"
                  alt="微信公众号二维码"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const placeholder = document.createElement('span');
                    placeholder.textContent = '二维码加载中';
                    (e.target as HTMLImageElement).parentElement?.appendChild(placeholder);
                  }}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-navy leading-relaxed">
                  {isDesktop 
                    ? t('modal.contact.pc_hint') 
                    : t('modal.contact.mobile_hint')
                  }
                </p>
              </div>

              {isDesktop && (
                <button
                  onClick={handleWechatPCLogin}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#07c160] px-6 py-4 text-base font-bold text-white shadow-card transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <FaWeixin size={20} />
                  {t('modal.contact.wechat_login_btn')}
                </button>
              )}

              <p className="text-xs text-slate-400 leading-relaxed">
                {t('modal.contact.wechat_hours')}
              </p>

              {/* メール/パスワードでのログイン入口 */}
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-xs text-slate-400 font-medium">或者</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <button
                onClick={() => { setShowWechatModal(false); setShowEmailAuthModal(true); }}
                className="w-full pb-2 text-sm font-semibold text-slate-400 hover:text-coral transition-colors"
              >
                邮箱登录注册 →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Details Modal */}
      {/* Info Modal */}
      {infoModal && (
        <div className="fixed inset-0 z-[1000] flex items-start sm:items-center justify-center p-4 sm:p-6 pt-20 sm:pt-6" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setInfoModal(null)} />
          <div className={`relative z-50 w-full max-w-3xl overflow-hidden rounded-[2.5rem] shadow-elevated animate-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-100px)] sm:max-h-[85vh] ${infoModal === 'contact' ? 'bg-coral' : 'bg-white'}`}>
            <div className={`px-8 pt-8 pb-4 shrink-0 flex justify-between items-center border-b rounded-t-[2.5rem] ${infoModal === 'contact' ? 'bg-coral text-white border-coral' : 'bg-white border-slate-100 text-navy'}`}>
              <h3 className="text-2xl font-bold">
                {infoModal === 'operator' && t('modal.operator.title')}
                {infoModal === 'privacy' && t('modal.privacy.title')}
                {infoModal === 'legal' && t('modal.legal.title')}
                {infoModal === 'terms' && termsTitle[currentLang]}
                {infoModal === 'contact' && t('modal.contact.title')}
              </h3>
              <button 
                onClick={() => setInfoModal(null)} 
                className={`h-10 w-10 flex items-center justify-center rounded-full transition-all ${infoModal === 'contact' ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-slate-100 text-slate-400 hover:text-navy hover:bg-slate-200'}`}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="px-8 pt-8 pb-16 overflow-y-auto custom-scrollbar bg-white rounded-b-[2.5rem]">
              <div className="space-y-8">
                {infoModal === 'operator' && (
                  <div className="space-y-8">
                    <section>
                      <h4 className="text-sm font-bold text-navy mb-4 border-l-4 border-coral pl-3">{t('modal.operator.company_overview')}</h4>
                      <div className="divide-y divide-slate-50 border-t border-slate-50">
                        <div className="grid gap-4 sm:grid-cols-[140px,1fr] py-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.distributor')}</div>
                          <div className="text-sm text-navy font-semibold">SMHC株式会社</div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-[140px,1fr] py-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.representative')}</div>
                          <div className="text-sm text-navy font-semibold">李 鉉道</div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-[140px,1fr] py-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.address_label')}</div>
                          <div className="text-sm text-navy font-semibold">
                            {t('modal.operator.address_value')}
                          </div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-[140px,1fr] py-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.phone')}</div>
                          <div className="text-sm text-navy font-semibold">03-6908-6228</div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-[140px,1fr] py-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.email')}</div>
                          <div className="text-sm text-navy font-semibold">online-clinic@smhc.co.jp</div>
                        </div>
                      </div>
                    </section>

                    <section>
                      <h4 className="text-sm font-bold text-navy mb-4 border-l-4 border-coral pl-3">{t('modal.operator.partner_institution')}</h4>
                      <div className="rounded-2xl bg-slate-50 p-6 border border-slate-100">
                        <div className="grid gap-2">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.institution_name')}</div>
                          <div className="text-base text-navy font-bold">大日向医院</div>
                        </div>
                        <div className="grid gap-2 mt-4">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('modal.operator.managing_doctor')}</div>
                          <div className="text-sm text-navy font-semibold">大日向 玲紀</div>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-4 leading-relaxed italic">
                          {t('modal.operator.partner_note')}
                        </p>
                      </div>
                    </section>
                  </div>
                )}

                {infoModal === 'privacy' && (
                  <div className="prose prose-sm max-w-none text-slate-600 leading-relaxed space-y-8">
                    <p className="text-sm font-medium">
                      {t('modal.privacy.intro')}
                    </p>
                    
                    <div className="space-y-6">
                      {[
                        { 
                          title: t('modal.privacy.section1.title'),
                          body: t('modal.privacy.section1.body')
                        },
                        { 
                          title: t('modal.privacy.section2.title'),
                          body: t('modal.privacy.section2.body')
                        },
                        { 
                          title: t('modal.privacy.section3.title'),
                          list: t('modal.privacy.section3.list', { returnObjects: true }) as string[]
                        }
                      ].map((section, idx) => (
                        <section key={idx} className="space-y-3">
                          <h4 className="text-base font-bold text-navy">{section.title}</h4>
                          {section.body && <p className="text-sm">{section.body}</p>}
                          {section.list && (
                            <ul className="list-disc pl-5 space-y-1">
                              {section.list.map((item, i) => <li key={i} className="text-sm">{item}</li>)}
                            </ul>
                          )}
                        </section>
                      ))}
                    </div>

                    <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showFullPrivacy ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="space-y-8 pt-8 border-t border-slate-100">
                        {[
                          {
                            title: t('modal.privacy.section4.title'),
                            list: t('modal.privacy.section4.list', { returnObjects: true }) as string[]
                          },
                          {
                            title: t('modal.privacy.section5.title'),
                            body: t('modal.privacy.section5.body')
                          },
                          {
                            title: t('modal.privacy.section6.title'),
                            list: t('modal.privacy.section6.list', { returnObjects: true }) as string[]
                          },
                          {
                            title: t('modal.privacy.section7.title'),
                            body: t('modal.privacy.section7.body')
                          },
                          {
                            title: t('modal.privacy.section8.title'),
                            body: t('modal.privacy.section8.body')
                          }
                        ].map((section, idx) => (
                          <section key={idx} className="space-y-3">
                            <h4 className="text-base font-bold text-navy">{section.title}</h4>
                            {section.body && <p className="text-sm">{section.body}</p>}
                            {section.list && (
                              <ul className="list-disc pl-5 space-y-1">
                                {section.list.map((item, i) => <li key={i} className="text-sm">{item}</li>)}
                              </ul>
                            )}
                          </section>
                        ))}
                      </div>
                    </div>

                    <button 
                      onClick={() => setShowFullPrivacy(!showFullPrivacy)}
                      className="w-full py-4 flex items-center justify-center gap-2 text-sm font-bold text-coral hover:bg-coral/5 rounded-2xl transition-all border border-dashed border-coral/30"
                    >
                      {showFullPrivacy 
                        ? t('modal.privacy.show_less')
                        : t('modal.privacy.show_full')}
                      <RefreshCcw size={16} className={`transition-transform duration-500 ${showFullPrivacy ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}

                {infoModal === 'legal' && (
                  <div className="space-y-6">
                    <div className="grid gap-4 divide-y divide-slate-50 border-y border-slate-50">
                      {[
                        { 
                          l: t('modal.legal.price_label'), 
                          v: t('modal.legal.price_value')
                        },
                        { 
                          l: t('modal.legal.fees_label'), 
                          v: t('modal.legal.fees_value')
                        },
                        { 
                          l: t('modal.legal.payment_label'), 
                          v: t('modal.legal.payment_value')
                        },
                        { 
                          l: t('modal.legal.timing_label'), 
                          v: t('modal.legal.timing_value')
                        },
                        { 
                          l: t('modal.legal.delivery_label'), 
                          v: t('modal.legal.delivery_value')
                        },
                        { 
                          l: t('modal.legal.returns_label'), 
                          v: t('modal.legal.returns_value')
                        },
                      ].map((row, i) => (
                        <div key={i} className="py-4 grid gap-2 sm:grid-cols-[140px,1fr]">
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{row.l}</span>
                          <span className="text-sm text-navy font-semibold leading-relaxed">{row.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {infoModal === 'terms' && (
                  <div className="space-y-6">
                    <p className="text-sm text-slate-500 leading-relaxed">{termsIntro[currentLang]}</p>
                    {termsArticles.map((a, i) => (
                      <div key={i} className="space-y-2">
                        <h4 className="text-sm font-bold text-navy">{a.title[currentLang]}</h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 leading-relaxed">
                          {a.body[currentLang].map((line, j) => (
                            <li key={j}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {infoModal === 'contact' && (
                  <div className="space-y-6">
                    {contactStep === 'guide' ? (
                      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        {/* Status Alert */}
                        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3">
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-amber-500 text-white flex items-center justify-center mt-0.5">
                            <Clock size={12} strokeWidth={3} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-amber-800 leading-tight">
                              {t('modal.contact.guide_title')}
                            </p>
                            <p className="mt-1 text-xs text-amber-700/80 leading-relaxed font-medium">
                              {t('modal.contact.guide_desc')}
                            </p>
                          </div>
                        </div>

                        {/* Reservation Buttons */}
                        <div className="grid gap-4">
                          {currentLang === 'ja' ? (
                            <button
                              onClick={() => { setInfoModal(null); setShowLoginModal(true); }}
                              className="w-full flex items-center justify-between p-5 bg-[#06C755] hover:bg-[#05b14c] text-white rounded-3xl shadow-lg shadow-green-500/10 transition-all hover:scale-[1.02] active:scale-95 group"
                            >
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center">
                                  <SiLine size={24} />
                                </div>
                                <div className="text-left">
                                  <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Reservation</div>
                                  <div className="text-lg font-bold leading-tight">{t('modal.contact.action_online')}</div>
                                </div>
                              </div>
                              <div className="h-10 w-10 rounded-full bg-black/5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setInfoModal(null);
                                setTimeout(() => handleWechatConsult(), 200);
                              }}
                              className="w-full flex items-center justify-between p-5 bg-[#07c160] hover:bg-[#06af56] text-white rounded-3xl shadow-lg shadow-green-600/10 transition-all hover:scale-[1.02] active:scale-95 group"
                            >
                              <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center">
                                  <FaWeixin size={24} />
                                </div>
                                <div className="text-left">
                                  <div className="text-[10px] font-black uppercase tracking-widest opacity-70">WeChat</div>
                                  <div className="text-lg font-bold leading-tight">{t('modal.contact.action_online')}</div>
                                </div>
                              </div>
                              <div className="h-10 w-10 rounded-full bg-black/5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </button>
                          )}
                        </div>

                        {/* Secondary Entry for the form */}
                        <div className="text-center pt-2">
                          <button 
                            type="button"
                            onClick={() => setContactStep('form')}
                            className="text-xs font-bold text-slate-400 hover:text-coral transition-colors underline-offset-4 hover:underline"
                          >
                            {t('modal.contact.use_form_link')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleContactSubmit} className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <button 
                          type="button"
                          onClick={() => setContactStep('guide')}
                          className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-coral transition-colors mb-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                          </svg>
                          {t('modal.contact.back_to_guide')}
                        </button>

                        <div>
                          <label className="block text-sm font-bold text-navy mb-2">
                            {t('contact.name')}
                          </label>
                          <input
                            type="text"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 px-5 py-4 text-navy placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral transition-all font-sans"
                            placeholder={t('contact.name_placeholder', { defaultValue: '' }) || undefined}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-navy mb-2">
                            {t('contact.email')}
                          </label>
                          <input
                            type="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 px-5 py-4 text-navy placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral transition-all font-sans"
                            placeholder={t('contact.email_placeholder', { defaultValue: '' }) || undefined}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-navy mb-2">
                            {t('contact.message')}
                          </label>
                          <textarea
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                            rows={5}
                            className="w-full rounded-3xl border border-slate-200 px-5 py-4 text-navy placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral transition-all resize-none font-sans"
                            placeholder={t('contact.message_placeholder', { defaultValue: '' }) || undefined}
                          />
                          <p className="mt-3 text-[11px] text-slate-400 font-medium">
                            {t('contact.note', { defaultValue: '' })}
                          </p>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-primary px-8 py-5 text-lg font-bold text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100"
                        >
                          {isSubmitting ? (
                            <div className="flex items-center gap-2">
                              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                              {t('contact.sending')}
                            </div>
                          ) : (
                            <>
                              <Send size={20} />
                              {t('contact.send')}
                            </>
                          )}
                        </button>
                        
                        <div className="pt-4 text-center">
                          <p className="text-xs text-slate-400">
                            Email: online-clinic@smhc.co.jp
                          </p>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 招待リンクモーダル（?ref= / ?lc= アクセス時に表示） */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[1100] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={() => setShowInviteModal(false)} />
          <div className="relative z-50 w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated animate-in zoom-in-95 duration-200">
            <div className="px-8 pt-8 pb-4 flex justify-between items-center border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-coral/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="text-coral w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-navy leading-tight">{t('invite.landing_title')}</h3>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="rounded-full p-2 bg-slate-100 text-slate-400 hover:bg-slate-200 transition-all flex-shrink-0 ml-2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-8 py-6 space-y-3">
              <p className="text-sm text-slate-500 leading-relaxed pb-2">{t('invite.landing_desc')}</p>
              <button
                onClick={() => { setShowInviteModal(false); handleLineLogin(); }}
                className="w-full bg-[#06C755] hover:bg-[#05b14c] text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-500/20"
              >
                <SiLine size={22} />
                {t('auth.login_line')}
              </button>
              <button
                onClick={() => { setShowInviteModal(false); handleWechatInviteLogin(); }}
                className="w-full bg-[#07C160] hover:bg-[#06a854] text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-400/20"
              >
                <FaWeixin size={22} />
                {t('auth.login_wechat')}
              </button>
              <button
                onClick={() => { setShowInviteModal(false); setEmailAuthDefaultTab('register'); setShowEmailAuthModal(true); }}
                className="w-full bg-coral hover:opacity-90 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-coral/20"
              >
                <FaEnvelope size={18} />
                {t('invite.register_email')}
              </button>
              <button
                onClick={() => setShowInviteModal(false)}
                className="w-full text-slate-400 text-sm hover:text-slate-600 transition pt-1"
              >
                {t('common.cancel')}
              </button>
            </div>
            <div className="px-8 pb-6 flex items-center justify-center gap-2 text-xs text-slate-400">
              <ShieldCheck size={14} />
              <span>{t('invite.safe_hint')}</span>
            </div>
          </div>
        </div>
      )}

      {selectedService && (
        <div className="fixed inset-0 z-[1000] flex items-start sm:items-center justify-center p-4 sm:p-6 pt-20 sm:pt-6" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-navy/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
            aria-hidden="true" 
            onClick={() => setSelectedService(null)}
          />

          {/* Modal panel */}
          <div className="relative z-50 w-full max-w-2xl overflow-hidden rounded-[2.5rem] bg-white shadow-elevated animate-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-100px)] sm:max-h-[90vh]">
            {/* Header/Image Area */}
            <div className="relative h-48 sm:h-56 shrink-0 overflow-hidden">
              <img 
                src={serviceImages[selectedService.imageKey]} 
                alt={selectedService.title} 
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
              
              <button
                type="button"
                className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md transition-all hover:bg-black/40 hover:scale-110 focus:outline-none z-10"
                onClick={() => setSelectedService(null)}
              >
                <X className="h-6 w-6" />
                <span className="sr-only">Close</span>
              </button>

              <div className="absolute bottom-6 left-8 right-8">
                <div className="inline-flex items-center rounded-full bg-coral/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-md mb-3 border border-white/20">
                  {t('lp.services.eyebrow')}
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" id="modal-title">
                  {selectedService.title}
                </h3>
              </div>
            </div>

            {/* Content Area - Scrollable */}
            <div className="px-8 py-8 overflow-y-auto custom-scrollbar">
              <div className="space-y-10">
                {/* Description */}
                <section>
                  <p className="text-lg leading-relaxed text-slate-600 font-medium whitespace-pre-wrap">
                    {selectedService.modal?.description || selectedService.body}
                  </p>
                </section>

                {/* Split Layout: Left Col (Features, Recommends) | Right Col (Prices) */}
                <div className="grid gap-6 md:grid-cols-2 items-start">
                  
                  {/* Left Column */}
                  <div className="flex flex-col gap-6">
                    {/* Features */}
                    <div className="rounded-3xl bg-slate-50 p-6 border border-slate-100 shadow-sm">
                      <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-navy mb-5">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-coral/10 text-coral text-xs">✨</span>
                        {t('modal.service.points')}
                      </h4>
                      <ul className="space-y-3">
                        {(selectedService.modal?.features || [selectedService.body]).map((feat, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-slate-500 leading-snug">
                            <CheckCircle2 className="h-4 w-4 text-coral shrink-0 mt-0.5" />
                            {feat}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Recommendations */}
                    {selectedService.modal?.recommendations && (
                      <div className="rounded-3xl bg-navy/5 p-6 border border-navy/5">
                        <h4 className="text-base font-bold text-navy mb-5 flex items-center gap-2">
                          <span className="text-xl">🎯</span>
                          {t('modal.service.recommended_for')}
                        </h4>
                        <ul className="grid gap-3 sm:grid-cols-1">
                          {selectedService.modal.recommendations.map((item, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm text-slate-600 bg-white/50 rounded-2xl p-4 border border-white">
                              <div className="h-1.5 w-1.5 rounded-full bg-coral shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Prices Nested Accordion */}
                  <div className="rounded-3xl bg-slate-50 p-6 border border-slate-100 shadow-sm">
                    <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-navy mb-5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs">¥</span>
                      {t('modal.service.reference_price')}
                    </h4>
                    <div className="space-y-4">
                      {(modalPriceGroups.length > 0 ? modalPriceGroups : (selectedService.modal?.priceGroups ?? [])).map((group, g_idx) => (
                        <div key={g_idx} className={`rounded-2xl border transition-all duration-300 ${openPriceIndex === g_idx ? 'border-primary/20 bg-primary/5 shadow-sm' : 'border-slate-200 bg-white hover:border-primary/10'}`}>
                          {/* Level 1: Medicine Group */}
                          <button
                            onClick={() => {
                              if (openPriceIndex === g_idx) {
                                setOpenPriceIndex(null);
                              } else {
                                setOpenPriceIndex(g_idx);
                                setOpenPriceVariant(`${g_idx}-0`); // Auto-open first variant
                              }
                            }}
                            className="flex w-full items-center justify-between p-4 text-left"
                          >
                            <span className="text-sm font-bold text-navy pr-4">{group.name}</span>
                            <div className={`flex-shrink-0 transition-transform duration-300 ${openPriceIndex === g_idx ? 'rotate-180 text-primary' : 'text-slate-400'}`}>
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>
                          
                          {/* Level 1 Content */}
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openPriceIndex === g_idx ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-4 pt-1 flex flex-col gap-3 border-t border-primary/10 mt-2">
                              {group.variants.map((price, v_idx) => {
                                const variantId = `${g_idx}-${v_idx}`;
                                const isVariantOpen = openPriceVariant === variantId;
                                
                                return (
                                  <div key={v_idx} className={`bg-white rounded-xl border transition-all duration-300 ${isVariantOpen ? 'border-primary/20 shadow-sm' : 'border-slate-100 hover:border-primary/5'}`}>
                                    {/* Level 2: Dosage Variant */}
                                    <button
                                      onClick={() => setOpenPriceVariant(isVariantOpen ? null : variantId)}
                                      className="flex w-full items-center justify-between p-3 text-left"
                                    >
                                      <span className="text-xs font-semibold text-slate-700">{price.dosage}</span>
                                      <div className={`flex-shrink-0 transition-transform duration-300 ${isVariantOpen ? 'rotate-180 text-primary' : 'text-slate-300'}`}>
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </button>
                                    
                                    {/* Level 2 Content */}
                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isVariantOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                      <div className="px-3 flex flex-col gap-3 pb-3 border-t border-slate-50 mt-1 pt-3">
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
                                            {(() => {
                                              const num = parseInt(price.single.replace(/[^0-9]/g, '')) ;
                                              const taxIncl = Math.floor(num * 1.1);
                                              const formattedIncl = taxIncl.toLocaleString();
                                              const label = currentLang === 'ja' ? '税込' : '含税';
                                              const unit = currentLang === 'ja' ? '円' : ' 日元';
                                              return (
                                                <>
                                                  <span className="font-bold text-navy text-sm">
                                                    {price.single}
                                                  </span>
                                                  <span className="text-[11px] font-medium text-slate-400">
                                                    ({label} {formattedIncl}{unit})
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">
                        {t('modal.service.price_note')}
                      </p>
                    </div>
                  </div>
                </div>



                {/* FAQs - Linked to main FAQ modal */}
                <section className="rounded-3xl bg-slate-50 border border-slate-100 flex flex-col sm:flex-row items-center justify-between p-6 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-white shadow-soft flex items-center justify-center text-2xl shrink-0">
                      ❓
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-navy mb-1">
                        {t('lp.faq.title')}
                      </h4>
                      <p className="text-xs text-slate-500 font-medium">
                        {t('lp.faq.subtitle')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedService(null);
                      setIsFaqModalOpen(true);
                    }}
                    className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-navy shadow-soft hover:border-coral/30 hover:bg-coral/5 hover:text-coral transition-all"
                  >
                    {t('lp.faq.view_button')} <span aria-hidden="true">&rarr;</span>
                  </button>
                </section>

                {/* Disclaimer */}
                <div className="rounded-2xl bg-amber-50/50 p-5 border border-amber-100/50 italic">
                  <p className="text-[11px] text-amber-700/70 leading-relaxed flex gap-2">
                    <span className="shrink-0">※</span>
                    {t('modal.service.disclaimer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-8 py-8 shrink-0 bg-white border-t border-slate-50 flex justify-center items-center rounded-b-[2.5rem]">
              <div className="flex gap-3 w-full sm:w-auto">
                {localStorage.getItem('token') ? (
                  <a
                    href="/mypage"
                    className="flex-1 inline-flex items-center justify-center rounded-2xl bg-gradient-primary px-12 py-4 text-base font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                  >
                    {t('navbar.mypage')}
                  </a>
                ) : currentLang === 'zh' ? (
                  <button
                    type="button"
                    className="flex-1 inline-flex items-center justify-center rounded-2xl bg-[#07c160] px-12 py-4 text-base font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                    onClick={() => { setSelectedService(null); handleWechatConsult(); }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="22" height="22" className="mr-2">
                      <path d="M9.5 4C5.36 4 2 6.91 2 10.5c0 1.96 1 3.72 2.6 4.9l-.6 2.1 2.3-1.3c.72.21 1.46.3 2.2.3.24 0 .48-.01.72-.04-.15-.46-.22-.95-.22-1.46 0-3.31 2.91-6 6.5-6 .24 0 .48.01.7.04C15.55 6.64 12.77 4 9.5 4z"/>
                      <path d="M15.5 9.5c-3.04 0-5.5 2.01-5.5 4.5s2.46 4.5 5.5 4.5c.66 0 1.3-.1 1.88-.27l1.92 1.08-.47-1.74A4.36 4.36 0 0 0 21 14c0-2.49-2.46-4.5-5.5-4.5z"/>
                    </svg>
                    {t('lp.cta.wechat')}
                  </button>
                ) : (
                    <button
                      onClick={() => { setSelectedService(null); setShowLoginModal(true); }}
                      className="flex-1 inline-flex items-center justify-center rounded-2xl bg-[#06c755] px-12 py-4 text-base font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                    >
                      <SiLine className="mr-2 text-xl" /> {t('lp.cta.primary')}
                    </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;


